import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const formatearMoneda = (valor) => {
  if (valor === undefined || valor === null || isNaN(valor)) {
    return '$0,00';
  }
  return `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PasoPrecios = ({
  productosFacturar,
  totales,
  pedidosIds,
  pedidos,
  loading,
  onPreciosChange,
  onContinue,
  onClose
}) => {
  
  const handlePrecioChange = (nombreProducto, valor) => {
    const precio = valor === '' ? 0 : parseFloat(valor) || 0;
    
    const productosActualizados = productosFacturar.map(prod => 
      prod.nombre === nombreProducto 
        ? { ...prod, precio_unitario_con_iva: precio }
        : prod
    );
    
    onPreciosChange(productosActualizados);
  };

  const calcularPorcentajeIVA = () => {
    if (totales.total > 0) {
      return ((totales.iva / (totales.total - totales.iva)) * 100).toFixed(2);
    }
    return '0.00';
  };

  const validarPrecios = () => {
    const productosSinPrecio = productosFacturar.filter(
      prod => !prod.precio_unitario_con_iva || prod.precio_unitario_con_iva <= 0
    );

    if (productosSinPrecio.length > 0) {
      alert(`Debe ingresar el precio unitario para: ${productosSinPrecio.map(p => p.nombre).join(', ')}`);
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (validarPrecios()) {
      onContinue(productosFacturar);
    }
  };

  return (
    <>
      <DialogContent dividers>
        {productosFacturar.length > 0 ? (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Cantidad</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">% IVA</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Precio Unitario (con IVA)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Subtotal</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">IVA</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productosFacturar.map((producto, index) => {
                    const cantidad = producto.cantidad_total || 0;
                    const precioConIva = producto.precio_unitario_con_iva || 0;
                    
                    // Calcular total del renglón
                    const totalRenglon = cantidad * precioConIva;
                    
                    // Calcular porcentaje de IVA basado en los valores originales
                    const baseSinIva = producto.valor_facturacion_unitario || 0;
                    const ivaUnitario = producto.valor_iva_unitario || 0;
                    
                    let porcentajeIva = 0;
                    if (baseSinIva > 0) {
                      porcentajeIva = (ivaUnitario / baseSinIva) * 100;
                    }
                    
                    // ✅ FÓRMULA CORRECTA: subtotal = total / (1 + %IVA/100)
                    const subtotalRenglon = totalRenglon / (1 + parseFloat(producto.valor_facturacion_unitario) / 100);
                    const ivaRenglon = totalRenglon - subtotalRenglon;

                    return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {producto.nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {producto.pedidos_count || 0} pedido{producto.pedidos_count !== 1 ? 's' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" fontWeight="bold">
                            {cantidad}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="info.main">
                            {producto.valor_facturacion_unitario}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={precioConIva || ''}
                            onChange={(e) => handlePrecioChange(producto.nombre, e.target.value)}
                            placeholder="0.00"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                              inputProps: { 
                                min: 0, 
                                step: "0.01",
                                style: { textAlign: 'right' }
                              }
                            }}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatearMoneda(subtotalRenglon)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="info.main">
                            {formatearMoneda(ivaRenglon)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="bold" color="primary.main">
                            {formatearMoneda(totalRenglon)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Remitos incluidos:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pedidosIds?.map(id => {
                  const remito = pedidos?.find(p => p.id === id)?.remito_entrega;
                  return (
                    <Typography
                      key={id}
                      variant="caption"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: '#e3f2fd',
                        borderRadius: 1,
                        display: 'inline-block'
                      }}
                    >
                      {remito || 'S/N'}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Cargando productos...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Subtotal:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatearMoneda(totales.subtotal)}
            </Typography>
          </Box>
          
          <Divider orientation="vertical" flexItem sx={{ height: 30 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              IVA ({calcularPorcentajeIVA()}%):
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="info.main">
              {formatearMoneda(totales.iva)}
            </Typography>
          </Box>
          
          <Divider orientation="vertical" flexItem sx={{ height: 30 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              Total:
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {formatearMoneda(totales.total)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            {productosFacturar.length} producto{productosFacturar.length !== 1 ? 's' : ''}
          </Typography>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={
              productosFacturar.length === 0 ||
              productosFacturar.some(p => !p.precio_unitario_con_iva || p.precio_unitario_con_iva <= 0)
            }
            endIcon={<ArrowForwardIcon />}
          >
            Continuar
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default PasoPrecios;